import React, { useCallback, useEffect, useState } from 'react';
import { contactApi } from '../services';
import googleApi from '../services/googleApi';
import AccordionSection from './AccordionSection';
import { useAuth } from './AuthGate';
import Icon from './Icon';
import { managerInitials } from './RelationshipManagersField';
import './RelationshipManagers.css';
import './MeetingsSection.css';

const dayOf = (iso) => (iso && iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso));

const formatDay = (iso) => (iso ? dayOf(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const formatSince = (iso) => (iso ? dayOf(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : '');

const lengthOf = (meeting) => {
  if (meeting.all_day || !meeting.end_at) return null;
  const minutes = Math.round((new Date(meeting.end_at) - new Date(meeting.start_at)) / 60000);
  if (!(minutes > 0)) return null;
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60}` : ''}`;
};

const pad = (value) => String(value).padStart(2, '0');

const clockOf = (iso) => {
  const date = new Date(iso);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Date and optional times from the form as what the API takes: a date alone, or UTC date-times. */
const stamp = (date, time) => (time ? new Date(`${date}T${time}`).toISOString() : date);

/** Log or change a meeting that has no calendar event: when, who was there, and the notes Doc's link. */
const MeetingForm = ({ companyId, meeting, onSaved, onCancel }) => {
  const { user } = useAuth();
  const day = meeting ? (meeting.all_day ? meeting.start_at : meeting.start_at && new Date(meeting.start_at)) : null;
  const [title, setTitle] = useState(meeting?.title || '');
  const [date, setDate] = useState(day ? (meeting.all_day ? day : `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`) : '');
  const [start, setStart] = useState(meeting && !meeting.all_day ? clockOf(meeting.start_at) : '');
  const [end, setEnd] = useState(meeting && !meeting.all_day && meeting.end_at ? clockOf(meeting.end_at) : '');
  const [notes, setNotes] = useState(meeting?.notes_links?.[0]?.url || '');
  const [colleagues, setColleagues] = useState(null);
  const [people, setPeople] = useState(null);
  const [userIds, setUserIds] = useState(meeting ? meeting.tileward.map(item => item.user_id).filter(Boolean) : (user?.id ? [user.id] : []));
  const [contactIds, setContactIds] = useState(meeting ? meeting.people.map(item => item.contact_id) : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    googleApi.colleagues().then(data => setColleagues(data.colleagues)).catch(() => setColleagues([]));
    contactApi.getCompanyContacts(companyId).then(data => setPeople(data.contacts)).catch(() => setPeople([]));
  }, [companyId]);

  const toggle = (setter) => (id) => setter(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));

  const submit = async (event) => {
    event.preventDefault();
    if (!title.trim() || !date) {
      setError('Give the meeting a title and a date');
      return;
    }
    setSaving(true);
    setError(null);
    const fields = {
      title: title.trim(),
      start_at: stamp(date, start),
      end_at: end ? stamp(date, end) : (meeting ? '' : undefined),
      notes_url: notes.trim() || (meeting ? '' : undefined),
      user_ids: userIds,
      contact_ids: contactIds,
    };
    try {
      if (meeting) await googleApi.updateMeeting(meeting.id, fields);
      else await googleApi.logMeeting(companyId, fields);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form className="rm-picker meet-form" onSubmit={submit}>
      <input className="rm-input" type="text" value={title} onChange={e => setTitle(e.target.value)}
        placeholder="What was it? e.g. Coffee with Pat" aria-label="Meeting title" maxLength={300} disabled={saving} />
      <div className="meet-form__row">
        <label>Date <input className="rm-input" type="date" value={date} onChange={e => setDate(e.target.value)} disabled={saving} /></label>
        <label>From <input className="rm-input" type="time" value={start} onChange={e => setStart(e.target.value)} disabled={saving} /></label>
        <label>To <input className="rm-input" type="time" value={end} onChange={e => setEnd(e.target.value)} disabled={saving || !start} /></label>
      </div>
      <input className="rm-input" type="url" value={notes} onChange={e => setNotes(e.target.value)}
        placeholder="Link to the notes Doc (Google Docs or Drive)" aria-label="Link to the notes Doc" disabled={saving} />
      <fieldset className="rm-picker__options" disabled={saving}>
        <legend className="meet-form__legend">From Tileward</legend>
        {colleagues === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
        {(colleagues || []).map(item => (
          <label key={item.id} className="rm-picker__option">
            <input type="checkbox" checked={userIds.includes(item.id)} onChange={() => toggle(setUserIds)(item.id)} />
            <span className="rm-picker__label">{item.name}</span>
          </label>
        ))}
      </fieldset>
      <fieldset className="rm-picker__options" disabled={saving}>
        <legend className="meet-form__legend">From the company</legend>
        {people === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
        {people && people.length === 0 && <p className="placeholder-text">Nobody is on file at this company.</p>}
        {(people || []).map(item => (
          <label key={item.id} className="rm-picker__option">
            <input type="checkbox" checked={contactIds.includes(item.id)} onChange={() => toggle(setContactIds)(item.id)} />
            <span className="rm-picker__label">{item.name || `Contact ${item.id}`}{item.title && <span className="rm-picker__title"> · {item.title}</span>}</span>
          </label>
        ))}
      </fieldset>
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      <div className="rm-buttons">
        <button type="submit" className="rm-button rm-button--primary" disabled={saving}>
          <Icon name={saving ? 'loader' : 'check'} spin={saving} /> {meeting ? 'Save' : 'Log meeting'}
        </button>
        <button type="button" className="rm-button rm-button--quiet" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

const Meeting = ({ meeting, onOpenPerson, onEdit, onDelete }) => {
  const length = lengthOf(meeting);
  const invited = meeting.source === 'invited';
  return (
    <li className={`meet${invited ? ' meet--invited' : ''}`}>
      <div className="meet__line">
        <span className="meet__when">{formatDay(meeting.start_at)}{length ? ` · ${length}` : ''}</span>
        <span className="meet__title">{meeting.title}</span>
        {meeting.tileward.length > 0 && (
          <span className="meet__team">
            {meeting.tileward.map(item => (
              <span key={item.email || item.name} className="rm-avatar meet__avatar" title={item.name}>{managerInitials(item.name)}</span>
            ))}
          </span>
        )}
      </div>
      {invited && (
        <p className="meet__invited">
          <Icon name="mail" /> Invited, not on calendar
          <span className="meet__meta"> · from {meeting.invited.from || 'someone outside'}: {meeting.invited.subject}</span>
        </p>
      )}
      {(meeting.people.length > 0 || meeting.others.length > 0) && (
        <ul className="meet__people">
          {meeting.people.map(person => (
            <li key={person.contact_id}>
              {onOpenPerson
                ? <button type="button" className="rm-link" onClick={() => onOpenPerson(person.contact_id)}>{person.name}</button>
                : <strong>{person.name}</strong>}
              {person.title && <span className="meet__meta"> – {person.title}</span>}
            </li>
          ))}
          {meeting.others.map(other => (
            <li key={other.email}>
              {other.email}<span className="meet__meta"> · not on file</span>
            </li>
          ))}
        </ul>
      )}
      {(meeting.notes_links.length > 0 || meeting.editable) && (
        <div className="meet__actions">
          {meeting.notes_links.map(link => (
            <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="meet__notes">
              <Icon name="file-text" /> {link.title}
            </a>
          ))}
          {meeting.editable && (
            <>
              <button type="button" className="rm-icon-button" onClick={() => onEdit(meeting)} aria-label={`Change ${meeting.title}`} title="Change">
                <Icon name="pencil" />
              </button>
              <button type="button" className="rm-icon-button rm-icon-button--danger" onClick={() => onDelete(meeting)} aria-label={`Remove ${meeting.title}`} title="Remove">
                <Icon name="trash" />
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * A company's meetings from Google Calendar and those logged by hand: a summary line, five at a
 * time, and a + to log an impromptu one. Synced meetings are read-only.
 */
const MeetingsSection = ({ companyId, companyName, onOpenPerson, dataVersion = 0 }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // 'new', or the meeting being changed
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => googleApi.meetings(companyId)
    .then(result => {
      setData(result);
      setError(null);
    })
    .catch(err => setError(err.message)), [companyId]);

  useEffect(() => {
    setData(null);
    setForm(null);
    setError(null);
  }, [companyId]);

  useEffect(() => {
    if (companyId) load();
  }, [companyId, dataVersion, load]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      const more = await googleApi.meetings(companyId, data.meetings[data.meetings.length - 1].id);
      setData(current => ({ ...current, meetings: [...current.meetings, ...more.meetings], has_more: more.has_more }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const remove = async (meeting) => {
    if (!window.confirm(`Remove "${meeting.title}" from the meetings?`)) return;
    try {
      await googleApi.deleteMeeting(meeting.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const saved = () => {
    setForm(null);
    load();
  };

  const summary = data?.summary;
  return (
    <AccordionSection
      id="meet-section"
      className="meet-section"
      open={open}
      onToggle={() => setOpen(current => !current)}
      title="Meetings"
      actions={!form && (
        <button type="button" className="rm-icon-button" onClick={() => { setOpen(true); setForm('new'); }}
          aria-label={`Log a meeting with ${companyName || 'this company'}`} title="Log a meeting">
          <Icon name="plus" />
        </button>
      )}
    >
      {form && <MeetingForm companyId={companyId} meeting={form === 'new' ? null : form} onSaved={saved} onCancel={() => setForm(null)} />}
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!data && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}

      {data && data.meetings.length === 0 && !form && (
        <p className="rm-field__empty">No meetings with {companyName || 'this company'} since {formatSince(summary.since)}.</p>
      )}

      {data && data.meetings.length > 0 && (
        <>
          <p className="meet-summary">
            {summary.last_met ? `Last met ${formatDay(summary.last_met)}` : 'Not met yet'}
            {summary.next && ` · Next: ${formatDay(summary.next.start_at)}`}
            {` · ${summary.total} since ${formatSince(summary.since)}`}
          </p>
          <ul className="meet-list">
            {data.meetings.map(meeting => (
              <Meeting key={meeting.id} meeting={meeting} onOpenPerson={onOpenPerson} onEdit={setForm} onDelete={remove} />
            ))}
          </ul>
          {data.has_more && (
            <button type="button" className="rm-link" onClick={showMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          )}
        </>
      )}
    </AccordionSection>
  );
};

export default MeetingsSection;
